import React from 'react';
import PropTypes from 'prop-types';
import { Anchor, Section, Container, Grid, Typography, Card } from 'components/base';
import {
  Accordion,
  CustomerReview,
  HtmlContent,
  ProductCard,
  ProductListCarousel,
} from 'components/rich';
import useDataContext from 'data/context/data';
import useClaimedCoupon from 'ecommerce/context/claimedCoupon';
import { couponSale } from 'ecommerce/lib/coupon';

import { ProductDetailLink } from '../pageLinks';

import tracker from '../../lib/tracker';
import { isViewOnWallProduct, VIEW_ON_WALL_APP_STORE_URL } from '../../lib/viewOnWall.mjs';

/**
 * PDP layout below the top section for format 1 content files (not migrated
 * to the redesign, see docs/product-page.md). The top section is
 * passed as children so it keeps sharing the `.section-product` container.
 */
const LegacyProductSections = ({ product, skuProduct, content, children }) => {
  const claimedCoupon = useClaimedCoupon();
  const dataContext = useDataContext();

  const mightLikeProducts = dataContext.getProducts(product.related);
  const offersViewOnWall = isViewOnWallProduct(product.id);

  const handleViewOnWallAppStoreClick = () => {
    tracker.onCustomEvent('view_on_wall_pdp_cta_click', {
      cta: 'app_store',
      location: 'badge',
      product_id: product.id,
    });
  };

  return (
    <>
      <Section className="section-product">
        <Container>
          {children}

          <Grid.Row className="mt-2">
            <Grid.Col xs={12} lg={7} className="pr-lg-2">
              {(content.spec.length > 0 || (offersViewOnWall && skuProduct)) && (
                <Card className="mb-1">
                  <Card.Body className="text-body">
                    <HtmlContent html={content.spec} />
                    {offersViewOnWall && skuProduct && (
                      <div className="wall-preview-promo">
                        <span className="wall-preview-promo__badge">View on Wall</span>
                        <span className="wall-preview-promo__microcopy">
                          In the Parabo Press app
                        </span>
                        <p className="wall-preview-promo__benefit">
                          See your photo and crop on your own wall at real print size before you
                          order, using augmented reality on a supported iPhone or iPad.
                        </p>
                        <Anchor
                          href={VIEW_ON_WALL_APP_STORE_URL}
                          targetBlank
                          className="link-app-store"
                          onClick={handleViewOnWallAppStoreClick}
                        >
                          <img
                            src="/static/img/app-store-badge.svg"
                            alt="Download Parabo Press on the App Store"
                            width="120"
                            height="40"
                          />
                        </Anchor>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}
              {content.faq && (
                <Accordion title="FAQ">
                  {content.faq.map((faq) => (
                    <Accordion.Item key={faq.caption} title={faq.caption}>
                      <HtmlContent html={faq.description} />
                    </Accordion.Item>
                  ))}
                </Accordion>
              )}
            </Grid.Col>

            <Grid.Col xs={12} lg={5} className="pl-lg-2 col-product-data">
              {content.testimonial && (
                <CustomerReview className="mt-2 mt-lg-0">
                  <Typography.H3 className="customer-review-text customer-review-text-product">
                    {content.testimonial.quote}
                  </Typography.H3>
                  <CustomerReview.Customer>
                    <Typography.Span>{content.testimonial.author}</Typography.Span>
                  </CustomerReview.Customer>
                </CustomerReview>
              )}
            </Grid.Col>
          </Grid.Row>
        </Container>
      </Section>

      {mightLikeProducts.length > 0 && (
        <Section>
          <Container>
            {/* h2 section heading; .h3 keeps the original visual size */}
            <Typography.H2 className="h3 section-title">YOU MIGHT LIKE</Typography.H2>
            <ProductListCarousel
              products={mightLikeProducts}
              className="mb-2"
              renderItem={(mightLikeProduct) => (
                <ProductDetailLink productId={mightLikeProduct.id}>
                  <ProductCard
                    asLink
                    product={mightLikeProduct}
                    sale={couponSale(claimedCoupon.previewSummary, mightLikeProduct)}
                  />
                </ProductDetailLink>
              )}
            />
          </Container>
        </Section>
      )}

      {content.extraDescription && (
        <Section>
          <Container className="my-5">
            <HtmlContent className="text-editorial" html={content.extraDescription} />
          </Container>
        </Section>
      )}
    </>
  );
};

LegacyProductSections.propTypes = {
  product: PropTypes.object.isRequired,
  skuProduct: PropTypes.object,
  content: PropTypes.object.isRequired,
  children: PropTypes.node,
};

export default LegacyProductSections;
